import { useState, useCallback } from "react";
import {
  CustomerSearchItemEntity,
  CustomerSearchPagination,
  SearchCustomersUseCase,
} from "../../domain";
import { CustomerRepositoryImpl } from "../../data/repositories/CustomerRepositoryImpl";

// Create repository instance (singleton)
const customerRepository = new CustomerRepositoryImpl();

// Create use case
const searchCustomersUseCase = new SearchCustomersUseCase(customerRepository);

export interface UseCustomersReturn {
  customers: CustomerSearchItemEntity[];
  pagination: CustomerSearchPagination | null;
  loading: boolean;
  error: string | null;

  searchCustomers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) => Promise<void>;

  fetchCustomers: (
    query: string,
    options?: {
      customerId?: string;
      page?: number;
      limit?: number;
    }
  ) => Promise<void>;

  clearError: () => void;
  reset: () => void;
}

export const useCustomers = (): UseCustomersReturn => {
  const [customers, setCustomers] = useState<CustomerSearchItemEntity[]>([]);
  const [pagination, setPagination] = useState<CustomerSearchPagination | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Search customers with optional filters
   */
  const searchCustomers = useCallback(
    async (
      params: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
      } = {}
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const result = await searchCustomersUseCase.execute(params);

        if (!result.success) {
          throw new Error("Failed to search customers");
        }

        setCustomers(result.data);
        setPagination(result.pagination);
      } catch (error: any) {
        const errorMessage = error.message || "Failed to search customers";
        setError(errorMessage);
        console.error("Failed to search customers:", error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Fetch customers using vector similarity search (invoice-specific)
   */
  const fetchCustomers = useCallback(
    async (
      query: string,
      options?: {
        customerId?: string;
        page?: number;
        limit?: number;
      }
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const result = await customerRepository.fetchCustomers(query, options);

        if (!result.success) {
          throw new Error("Failed to fetch customers");
        }

        setCustomers(result.data);
        setPagination(result.pagination);
      } catch (error: any) {
        const errorMessage = error.message || "Failed to fetch customers";
        setError(errorMessage);
        console.error("Failed to fetch customers:", error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setCustomers([]);
    setPagination(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    customers,
    pagination,
    loading,
    error,
    searchCustomers,
    fetchCustomers,
    clearError,
    reset,
  };
};
